import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowClassParams, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions } from "../utility/gridHelper";
import { PslAssignmentProject, PslService } from "./psl-assignment.model";

interface RegionOption {
  RegionOid: string;
  RegionName: string;
  RegionPsl: number | null;
}

interface ProjectOption extends PslAssignmentProject {
  Label: string;
  PlanTypeLabel: string;
}

interface PslOption {
  PslId: number;
  Label: string;
  Category: string;
  IsRegionPsl: boolean;
}

const REGION_PSL_CATEGORY = "Region PSL";
const PROJECT_PSL_CATEGORY = "Project PSL";

@Component({
  standalone: false,
  templateUrl: "./assign-psl.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintAssignPslComponent extends GridViewBaseComponent implements OnInit {
  gridOptions!: GridOptions<PslService>;
  gridApi!: GridApi;
  initPageSize = 15;
  keyFilterStats: string = "filter:capacity-blueprint-assign-psl";

  allProjects: PslAssignmentProject[] = [];

  regions: RegionOption[] = [];
  selectedRegion: RegionOption | null = null;

  projects: ProjectOption[] = [];
  selectedProject: ProjectOption | null = null;

  pslOptions: PslOption[] = [];
  selectedPsl: PslOption | null = null;

  rowData: PslService[] = [];

  hasRowSelected = false;
  canAssign = false;

  // Guards against out-of-order service loads: only the latest request may apply its results.
  private pslLoadRequestId = 0;

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    const columnDefs: ColDef[] = [
      {
        headerName: "",
        field: "checkbox",
        width: 50,
        maxWidth: 50,
        checkboxSelection: (params) => this.isServiceSelectable(params.data as PslService),
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      { headerName: "Service Tree Id", field: "ServiceTreeId", flex: 2, filter: true, sortable: true },
      { headerName: "Product Type", field: "ProductType", flex: 1, filter: true, sortable: true },
      { headerName: "Blueprint Name", field: "BlueprintName", flex: 1, filter: true, sortable: true },
      { headerName: "Blueprint Type", field: "BlueprintType", flex: 1, filter: true, sortable: true },
      { headerName: "Service Team", field: "ServiceTeam", flex: 1, filter: true, sortable: true },
      {
        headerName: "Status",
        colId: "Status",
        width: 160,
        maxWidth: 200,
        filter: true,
        sortable: true,
        valueGetter: (params: any) => {
          const service = params.data as PslService;
          if (!service?.HasBlueprint) {
            return "No blueprint";
          }

          return service.IsAssigned ? "Assigned" : "Available";
        },
        tooltipValueGetter: (params: any) => {
          const service = params.data as PslService;
          if (!service?.HasBlueprint) {
            return "This service has no blueprint and cannot be assigned.";
          }

          return service.IsAssigned ? "This service is already assigned to the selected project." : "";
        },
      },
    ];

    this.gridOptions = extendDefaultOptions({
      columnDefs,
      paginationPageSize: this.initPageSize,
      enableRangeSelection: false,
      rowSelection: "multiple",
      animateRows: true,
      enableBrowserTooltips: true,
      isRowSelectable: (node) => this.isServiceSelectable(node.data as PslService),
      rowClassRules: {
        "row-disabled": (params: RowClassParams) => !this.isServiceSelectable(params.data as PslService),
      },
    });

    this.loadingService.setLoading(true);
    try {
      const projectsResponse = await this.apiService.getPslAssignmentProjects().toPromise();
      if (projectsResponse) {
        this.allProjects = projectsResponse;
        this.regions = this.buildRegions(projectsResponse);
      }
    } catch (error: any) {
      this.notificationService.error(`Failed to load projects: ${this.toErrorMessage(error)}`);
    } finally {
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  override onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi = params.api;
    this.gridApi.hideOverlay();
  }

  refreshData() {
    // Data is loaded on demand as the user drills through the cascade; nothing to refresh globally.
  }

  onRegionChanged(): void {
    this.selectedProject = null;
    this.selectedPsl = null;
    this.pslOptions = [];
    this.rowData = [];
    this.hasRowSelected = false;
    this.canAssign = false;

    this.projects = this.selectedRegion
      ? this.allProjects
          .filter((p) => p.RegionOid === this.selectedRegion!.RegionOid)
          .map((p) => this.toProjectOption(p))
      : [];
    this.cdr.detectChanges();
  }

  onProjectChanged(): void {
    this.selectedPsl = null;
    this.rowData = [];
    this.hasRowSelected = false;
    this.canAssign = false;
    this.pslOptions = this.buildPslOptions();

    if (this.selectedProject?.ProjectType == "NewRegion")
    {
      // The "Region PSL" is default-selected when available.
      const regionPsl = this.pslOptions.find((o) => o.IsRegionPsl);
      if (regionPsl) {
        this.selectedPsl = regionPsl;
        void this.onPslChanged();
      }
    }

    this.cdr.detectChanges();
  }

  async onPslChanged(): Promise<void> {
    const requestId = ++this.pslLoadRequestId;

    this.rowData = [];
    this.hasRowSelected = false;
    this.canAssign = false;
    if (!this.selectedPsl) {
      this.cdr.detectChanges();
      return;
    }

    const requestedPslId = this.selectedPsl.PslId;
    const requestedProjectId = this.selectedProject?.Id ?? 0;

    this.loadingService.setLoading(true);
    try {
      const services = await this.apiService.getPslServices(requestedPslId, requestedProjectId).toPromise();

      // Only apply if this is still the latest request and the selection hasn't changed.
      const stillCurrent =
        requestId === this.pslLoadRequestId &&
        this.selectedPsl?.PslId === requestedPslId &&
        (this.selectedProject?.Id ?? 0) === requestedProjectId;

      if (stillCurrent) {
        this.rowData = services ?? [];
      }
    } catch (error: any) {
      if (requestId === this.pslLoadRequestId) {
        this.notificationService.error(`Failed to load services: ${this.toErrorMessage(error)}`);
      }
    } finally {
      if (requestId === this.pslLoadRequestId) {
        this.loadingService.setLoading(false);
        this.cdr.detectChanges();
        setTimeout(() => this.gridApi?.autoSizeAllColumns(), 100);
      }
    }
  }

  onSelectionChanged(params: SelectionChangedEvent): void {
    this.hasRowSelected = params.api.getSelectedRows().length > 0;
    this.canAssign = this.hasRowSelected && !!this.selectedProject;
    this.cdr.detectChanges();
  }

  async onAssign(): Promise<void> {
    if (!this.selectedProject) {
      this.notificationService.warning("Please select a project.");
      return;
    }

    const selectedRows = (this.gridApi.getSelectedRows() as PslService[]).filter(
      (r) => r.HasBlueprint && !r.IsAssigned && r.BlueprintId != null
    );
    if (selectedRows.length === 0) {
      this.notificationService.warning("Please select at least one service to assign.");
      return;
    }

    const blueprintIds = selectedRows.map((r) => r.BlueprintId!);
    const filterModel = this.gridApi.getFilterModel();
    try {
      this.loadingService.setLoading(true);
      await this.apiService.assignBlueprintsToProject(this.selectedProject.Id, blueprintIds).toPromise();
      this.notificationService.success(
        `${blueprintIds.length} service(s) assigned to project ${this.selectedProject.Label}.`
      );
      this.gridApi.deselectAll();
      this.hasRowSelected = false;
      this.canAssign = false;

      // Reload so the just-assigned services show as "Assigned" (greyed and non-selectable).
      await this.onPslChanged();
      this.gridApi.setFilterModel(filterModel);
    } catch (error: any) {
      this.notificationService.error(`Failed to assign services: ${this.toErrorMessage(error)}`);
    } finally {
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  // ApiService.handleError emits a plain string via throwError, so render that directly and
  // fall back to structured error fields for any other thrown value.
  private toErrorMessage(error: any): string {
    if (typeof error === "string") {
      return error;
    }

    return error?.error || error?.message || "Unknown error";
  }

  // A service can be selected for assignment only when it has a blueprint and is not already assigned.
  private isServiceSelectable(service: PslService | undefined): boolean {
    return !!service?.HasBlueprint && !service.IsAssigned;
  }

  private buildRegions(projects: PslAssignmentProject[]): RegionOption[] {
    const emptyRegionOid = "00000000-0000-0000-0000-000000000000";
    const map = new Map<string, RegionOption>();
    for (const p of projects) {
      // Skip projects with no assigned region so they don't collapse into a bogus bucket.
      if (!p.RegionOid || p.RegionOid === emptyRegionOid || map.has(p.RegionOid)) {
        continue;
      }

      map.set(p.RegionOid, {
        RegionOid: p.RegionOid,
        RegionName: p.RegionName ?? p.RegionOid,
        RegionPsl: p.RegionPsl,
      });
    }

    return Array.from(map.values()).sort((a, b) => a.RegionName.localeCompare(b.RegionName));
  }

  private toProjectOption(p: PslAssignmentProject): ProjectOption {
    return {
      ...p,
      PlanTypeLabel: p.PlanType ?? "Unspecified",
      Label: `${p.Title} (${p.ProjectOid})`,
    };
  }

  private buildPslOptions(): PslOption[] {
    const options: PslOption[] = [];

    // "Region PSL" is a standalone category for NewRegion projects, pinned at the top.
    const regionPsl = this.selectedRegion?.RegionPsl ?? null;
    if (regionPsl != null && this.selectedProject?.ProjectType == "NewRegion") {
      options.push({
        PslId: regionPsl,
        Label: `Region PSL (${regionPsl})`,
        Category: REGION_PSL_CATEGORY,
        IsRegionPsl: true,
      });
    }

    // Project PSLs available for the selected region and project type.
    if (this.selectedProject) {
      const planType = this.selectedProject.PlanType?.toLowerCase() ?? "";
      const seen = new Set<number>();
      if (regionPsl != null) {
        seen.add(regionPsl);
      }

      this.allProjects
        .filter(
          (p) => p.RegionOid === this.selectedProject!.RegionOid && (p.PlanType?.toLowerCase() ?? "") === planType
        )
        .forEach((p) => {
          if (p.Psl != null && !seen.has(p.Psl)) {
            seen.add(p.Psl);
            options.push({
              PslId: p.Psl,
              Label: `${p.Psl}`,
              Category: PROJECT_PSL_CATEGORY,
              IsRegionPsl: false,
            });
          }
        });
    }

    return options;
  }

  @HostListener("window:resize")
  onResize() {
    setTimeout(() => this.gridApi?.autoSizeAllColumns(), 100);
  }
}
